import { useCallback } from 'react';
import { update } from '../services/item';
import { setOpenModales } from '../store/global';
import { getId } from '../utils/item';
import { useBoardCommandTick } from './useBoardCommandTick';
import { toMapRegionNodeId } from '../components/board/map/boardMapNodeIds.js';

export const useBoardMapEntityCommands = params => {
  const {
    dispatch,
    tab,
    mapState,
    persistMapField,
    recordBoardHistorySnapshot,
    clearSelection,
    setWidgetLayoutOverrides,
    displayedPins,
    displayedLinks,
    setDisplayedPins,
    setDisplayedLinks,
    displayedShapes,
    setDisplayedShapes,
    displayedRegions,
    setDisplayedRegions,
    displayedDrawings,
    setDisplayedDrawings,
    displayedGroups,
    setDisplayedGroups,
    activeGroupId,
    setActiveGroupId,
    selectedPin,
    selectedShape,
    selectedRegion,
    selectedPins = [],
    selectedLinks = [],
    selectedRegions = [],
    selectedShapes = [],
    selectedDrawings = [],
    selectedPinIds = [],
    selectedLinkIds = [],
    selectedShapeIds = [],
    selectedRegionIds = [],
    selectedDrawingIds = [],
    selectedWidgetIds = [],
    selectedGroupIds = [],
    attachMapNodeToActiveGroup,
    selectRegionOnly,
    regionDraftPoints,
    setRegionDraftPoints,
    setRegionDraftHoverPoint,
    pinLinkModalId,
    backgroundModalId,
    pinColorCommand,
    pinColorCommandTick,
    pinSizeCommand,
    pinSizeCommandTick,
    pinIconCommand,
    pinIconCommandTick,
    unlinkItemCommandTick,
    openLinkModalCommandTick,
    openBackgroundModalCommandTick,
    shapeTypeCommand,
    shapeTypeCommandTick,
    shapeColorCommand,
    shapeColorCommandTick,
    shapeLayerCommand,
    shapeLayerCommandTick,
    regionColorCommand,
    regionColorCommandTick,
    regionBorderStyleCommand,
    regionBorderStyleCommandTick,
    drawingColorCommand,
    drawingColorCommandTick,
    drawingBorderStyleCommand,
    drawingBorderStyleCommandTick,
    lineColorCommand,
    lineColorCommandTick,
    lineArrowsCommand,
    lineArrowsCommandTick,
    lastHandledPinColorCommandTickRef,
    lastHandledPinSizeCommandTickRef,
    lastHandledPinIconCommandTickRef,
    lastHandledUnlinkItemCommandTickRef,
    lastHandledOpenLinkModalCommandTickRef,
    lastHandledOpenBackgroundModalCommandTickRef,
    lastHandledShapeTypeCommandTickRef,
    lastHandledShapeColorCommandTickRef,
    lastHandledShapeLayerCommandTickRef,
    lastHandledRegionColorCommandTickRef,
    lastHandledRegionBorderStyleCommandTickRef,
    lastHandledDrawingColorCommandTickRef,
    lastHandledDrawingBorderStyleCommandTickRef,
    lastHandledLineColorCommandTickRef,
    lastHandledLineArrowsCommandTickRef,
  } = params;

  const updateLinksPatch = useCallback(
    async (linkIds, patch) => {
      const nextLinkIds = Array.isArray(linkIds)
        ? [...new Set(linkIds.filter(Boolean))]
        : [];
      if (!nextLinkIds.length) {
        return;
      }
      const nextLinks = displayedLinks.map(link =>
        nextLinkIds.includes(link.id)
          ? {
              ...link,
              ...(patch || {}),
            }
          : link,
      );
      setDisplayedLinks(nextLinks);
      await persistMapField('links', nextLinks);
    },
    [displayedLinks, persistMapField, setDisplayedLinks],
  );

  const updatePinsPatch = useCallback(
    async (pinIds, patch) => {
      const nextPinIds = Array.isArray(pinIds)
        ? [...new Set(pinIds.filter(Boolean))]
        : [];
      if (!nextPinIds.length) {
        return;
      }
      const nextPins = displayedPins.map(pin =>
        nextPinIds.includes(pin.id)
          ? {
              ...pin,
              ...(patch || {}),
            }
          : pin,
      );
      setDisplayedPins(nextPins);
      await persistMapField('pins', nextPins);
    },
    [displayedPins, persistMapField, setDisplayedPins],
  );

  const updatePinPatch = useCallback(
    async (pinId, patch) => {
      if (!pinId) {
        return;
      }
      await updatePinsPatch([pinId], patch);
    },
    [updatePinsPatch],
  );

  const updateLinkPatch = useCallback(
    async (linkId, patch) => {
      if (!linkId) {
        return;
      }
      await updateLinksPatch([linkId], patch);
    },
    [updateLinksPatch],
  );

  const updateRegionsPatch = useCallback(
    async (regionIds, patch) => {
      const nextRegionIds = Array.isArray(regionIds)
        ? [...new Set(regionIds.filter(Boolean))]
        : [];
      if (!nextRegionIds.length) {
        return;
      }
      const nextRegions = displayedRegions.map(region =>
        nextRegionIds.includes(region.id)
          ? {
              ...region,
              ...(patch || {}),
            }
          : region,
      );
      setDisplayedRegions(nextRegions);
      await persistMapField('regions', nextRegions);
    },
    [displayedRegions, persistMapField, setDisplayedRegions],
  );

  const updateRegionPatch = useCallback(
    async (regionId, patch) => {
      if (!regionId) {
        return;
      }
      await updateRegionsPatch([regionId], patch);
    },
    [updateRegionsPatch],
  );

  const updateShapesPatch = useCallback(
    async (shapeIds, patch) => {
      const nextShapeIds = Array.isArray(shapeIds)
        ? [...new Set(shapeIds.filter(Boolean))]
        : [];
      if (!nextShapeIds.length) {
        return;
      }
      const nextShapes = displayedShapes.map(shape =>
        nextShapeIds.includes(shape.id)
          ? {
              ...shape,
              ...(patch || {}),
            }
          : shape,
      );
      setDisplayedShapes(nextShapes);
      await persistMapField('shapes', nextShapes);
    },
    [displayedShapes, persistMapField, setDisplayedShapes],
  );

  const updateShapePatch = useCallback(
    async (shapeId, patch) => {
      if (!shapeId) {
        return;
      }
      await updateShapesPatch([shapeId], patch);
    },
    [updateShapesPatch],
  );

  const updateDrawingsPatch = useCallback(
    async (drawingIds, patch) => {
      const nextDrawingIds = Array.isArray(drawingIds)
        ? [...new Set(drawingIds.filter(Boolean))]
        : [];
      if (!nextDrawingIds.length) {
        return;
      }
      const nextDrawings = displayedDrawings.map(drawing =>
        nextDrawingIds.includes(drawing.id)
          ? {
              ...drawing,
              ...(patch || {}),
            }
          : drawing,
      );
      setDisplayedDrawings(nextDrawings);
      await persistMapField('drawings', nextDrawings);
    },
    [displayedDrawings, persistMapField, setDisplayedDrawings],
  );

  const finalizeRegionDraft = useCallback(async () => {
    if (regionDraftPoints.length < 3) {
      return;
    }
    const nextRegion = {
      id:
        typeof crypto !== 'undefined' &&
        typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `region_${Date.now()}`,
      label: '',
      points: regionDraftPoints,
      fill: 'secondary',
      fillOpacity: 0.2,
      stroke: 'secondary',
      strokeWidth: 2,
      linkedItemId: null,
      visible: true,
      locked: false,
    };
    const nextRegions = [...mapState.regions, nextRegion];
    const xs = regionDraftPoints.map(point => Number(point?.x) || 0);
    const ys = regionDraftPoints.map(point => Number(point?.y) || 0);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    const maxX = Math.max(...xs);
    const maxY = Math.max(...ys);
    const nextGroups = await attachMapNodeToActiveGroup({
      nodeId: toMapRegionNodeId(nextRegion.id),
      rect: {
        x: minX,
        y: minY,
        w: Math.max(1, maxX - minX),
        h: Math.max(1, maxY - minY),
      },
      persist: false,
    });
    await Promise.all([
      persistMapField('regions', nextRegions),
      nextGroups !== displayedGroups
        ? persistMapField('groups', nextGroups)
        : Promise.resolve(),
    ]);
    setRegionDraftPoints([]);
    setRegionDraftHoverPoint(null);
    selectRegionOnly(nextRegion.id);
  }, [
    attachMapNodeToActiveGroup,
    displayedGroups,
    mapState.regions,
    persistMapField,
    regionDraftPoints,
    selectRegionOnly,
    setRegionDraftHoverPoint,
    setRegionDraftPoints,
  ]);

  const applySelectionColor = useCallback(
    color => {
      if (!color) {
        return;
      }
      const pinIdsToUpdate = selectedPins
        .filter(pin => (pin?.color || 'secondary') !== color)
        .map(pin => pin.id);
      const shapeIdsToUpdate = selectedShapes
        .filter(shape => (shape?.stroke || 'secondary') !== color)
        .map(shape => shape.id);
      const regionIdsToUpdate = selectedRegions
        .filter(
          region =>
            (region?.fill || 'secondary') !== color ||
            (region?.stroke || 'secondary') !== color,
        )
        .map(region => region.id);
      const drawingIdsToUpdate = selectedDrawings
        .filter(drawing => (drawing?.stroke || 'secondary') !== color)
        .map(drawing => drawing.id);
      const linkIdsToUpdate = selectedLinks
        .filter(link => (link?.color || 'secondary') !== color)
        .map(link => link.id);

      if (pinIdsToUpdate.length) {
        updatePinsPatch(pinIdsToUpdate, { color });
      }
      if (shapeIdsToUpdate.length) {
        updateShapesPatch(shapeIdsToUpdate, { stroke: color });
      }
      if (regionIdsToUpdate.length) {
        updateRegionsPatch(regionIdsToUpdate, { fill: color, stroke: color });
      }
      if (drawingIdsToUpdate.length) {
        updateDrawingsPatch(drawingIdsToUpdate, { stroke: color });
      }
      if (linkIdsToUpdate.length) {
        updateLinksPatch(linkIdsToUpdate, { color });
      }
    },
    [
      selectedDrawings,
      selectedLinks,
      selectedPins,
      selectedRegions,
      selectedShapes,
      updateDrawingsPatch,
      updateLinksPatch,
      updatePinsPatch,
      updateRegionsPatch,
      updateShapesPatch,
    ],
  );

  const applySelectionBorderStyle = useCallback(
    style => {
      if (!style) {
        return;
      }
      const borderPatchByStyle = {
        none: { strokeStyle: 'none', strokeWidth: 0, strokeDasharray: null },
        thick: { strokeStyle: 'thick', strokeWidth: 4, strokeDasharray: null },
        dotted: { strokeStyle: 'dotted', strokeWidth: 2, strokeDasharray: '1 6' },
        dashed: { strokeStyle: 'dashed', strokeWidth: 2, strokeDasharray: '8 6' },
        solid: { strokeStyle: 'solid', strokeWidth: 2, strokeDasharray: null },
      };
      const nextPatch = borderPatchByStyle[style] || borderPatchByStyle.solid;
      const regionIdsToUpdate = selectedRegions
        .filter(
          region =>
            region?.strokeStyle !== nextPatch.strokeStyle ||
            Number(region?.strokeWidth) !== nextPatch.strokeWidth ||
            (region?.strokeDasharray || null) !== nextPatch.strokeDasharray,
        )
        .map(region => region.id);
      const drawingIdsToUpdate = selectedDrawings
        .filter(
          drawing =>
            drawing?.strokeStyle !== nextPatch.strokeStyle ||
            Number(drawing?.strokeWidth) !== nextPatch.strokeWidth ||
            (drawing?.strokeDasharray || null) !== nextPatch.strokeDasharray,
        )
        .map(drawing => drawing.id);
      const shapeIdsToUpdate = selectedShapes
        .filter(
          shape =>
            shape?.strokeStyle !== nextPatch.strokeStyle ||
            Number(shape?.strokeWidth) !== nextPatch.strokeWidth ||
            (shape?.strokeDasharray || null) !== nextPatch.strokeDasharray,
        )
        .map(shape => shape.id);
      const linkIdsToUpdate = selectedLinks
        .filter(
          link =>
            link?.strokeStyle !== nextPatch.strokeStyle ||
            Number(link?.strokeWidth) !== nextPatch.strokeWidth ||
            (link?.strokeDasharray || null) !== nextPatch.strokeDasharray,
        )
        .map(link => link.id);

      if (regionIdsToUpdate.length) {
        updateRegionsPatch(regionIdsToUpdate, nextPatch);
      }
      if (drawingIdsToUpdate.length) {
        updateDrawingsPatch(drawingIdsToUpdate, nextPatch);
      }
      if (shapeIdsToUpdate.length) {
        updateShapesPatch(shapeIdsToUpdate, nextPatch);
      }
      if (linkIdsToUpdate.length) {
        updateLinksPatch(linkIdsToUpdate, nextPatch);
      }
    },
    [
      selectedDrawings,
      selectedLinks,
      selectedRegions,
      selectedShapes,
      updateDrawingsPatch,
      updateLinksPatch,
      updateRegionsPatch,
      updateShapesPatch,
    ],
  );

  const deletePins = useCallback(
    async (pinIds, options = {}) => {
      const { clearAfter = true } = options;
      const nextPinIds = Array.isArray(pinIds)
        ? [...new Set(pinIds.filter(Boolean))]
        : [];
      if (!nextPinIds.length) {
        return;
      }
      const nextPins = displayedPins.filter(pin => !nextPinIds.includes(pin.id));
      setDisplayedPins(nextPins);
      if (clearAfter) {
        clearSelection();
      }
      dispatch(setOpenModales({ id: pinLinkModalId, open: false }));
      await persistMapField('pins', nextPins);
    },
    [
      clearSelection,
      dispatch,
      displayedPins,
      persistMapField,
      pinLinkModalId,
      setDisplayedPins,
    ],
  );

  const deleteRegion = useCallback(
    async (regionIds, options = {}) => {
      const { clearAfter = true } = options;
      const nextRegionIds = Array.isArray(regionIds)
        ? [...new Set(regionIds.filter(Boolean))]
        : [];
      if (!nextRegionIds.length) {
        return;
      }
      const nextRegions = displayedRegions.filter(
        region => !nextRegionIds.includes(region.id),
      );
      setDisplayedRegions(nextRegions);
      if (clearAfter) {
        clearSelection();
      }
      await persistMapField('regions', nextRegions);
    },
    [clearSelection, displayedRegions, persistMapField, setDisplayedRegions],
  );

  const deleteShape = useCallback(
    async (shapeIds, options = {}) => {
      const { clearAfter = true } = options;
      const nextShapeIds = Array.isArray(shapeIds)
        ? [...new Set(shapeIds.filter(Boolean))]
        : [];
      if (!nextShapeIds.length) {
        return;
      }
      const nextShapes = displayedShapes.filter(
        shape => !nextShapeIds.includes(shape.id),
      );
      setDisplayedShapes(nextShapes);
      if (clearAfter) {
        clearSelection();
      }
      await persistMapField('shapes', nextShapes);
    },
    [clearSelection, displayedShapes, persistMapField, setDisplayedShapes],
  );

  const deleteLinks = useCallback(
    async (linkIds, options = {}) => {
      const { clearAfter = true } = options;
      const nextLinkIds = Array.isArray(linkIds)
        ? [...new Set(linkIds.filter(Boolean))]
        : [];
      if (!nextLinkIds.length) {
        return;
      }
      const nextLinks = displayedLinks.filter(
        link => !nextLinkIds.includes(link.id),
      );
      setDisplayedLinks(nextLinks);
      if (clearAfter) {
        clearSelection();
      }
      await persistMapField('links', nextLinks);
    },
    [clearSelection, displayedLinks, persistMapField, setDisplayedLinks],
  );

  const deleteDrawing = useCallback(
    async (drawingIds, options = {}) => {
      const { clearAfter = true } = options;
      const nextDrawingIds = Array.isArray(drawingIds)
        ? [...new Set(drawingIds.filter(Boolean))]
        : [];
      if (!nextDrawingIds.length) {
        return;
      }
      const nextDrawings = displayedDrawings.filter(
        drawing => !nextDrawingIds.includes(drawing.id),
      );
      setDisplayedDrawings(nextDrawings);
      if (clearAfter) {
        clearSelection();
      }
      await persistMapField('drawings', nextDrawings);
    },
    [clearSelection, displayedDrawings, persistMapField, setDisplayedDrawings],
  );

  const deleteWidget = useCallback(
    async (widgetIds, options = {}) => {
      const { clearAfter = true } = options;
      const nextWidgetIds = Array.isArray(widgetIds)
        ? [...new Set(widgetIds.filter(Boolean))]
        : [];
      if (!nextWidgetIds.length || !tab?._id || !Array.isArray(tab?.grid)) {
        return;
      }
      const nextGrid = tab.grid.filter(
        gridObj => !nextWidgetIds.includes(getId(gridObj)),
      );
      setWidgetLayoutOverrides(prev => {
        const hasAnyOverride = nextWidgetIds.some(widgetId => prev?.[widgetId]);
        if (!hasAnyOverride) {
          return prev;
        }
        const nextOverrides = { ...prev };
        nextWidgetIds.forEach(widgetId => {
          delete nextOverrides[widgetId];
        });
        return nextOverrides;
      });
      if (clearAfter) {
        clearSelection();
      }
      await update(`items/${tab._id}`, 'grid', nextGrid);
      recordBoardHistorySnapshot?.();
    },
    [clearSelection, recordBoardHistorySnapshot, setWidgetLayoutOverrides, tab],
  );

  const deleteGroup = useCallback(
    async (groupIds, options = {}) => {
      const { clearAfter = true } = options;
      const nextGroupIds = Array.isArray(groupIds)
        ? [...new Set(groupIds.filter(Boolean))]
        : [];
      if (!nextGroupIds.length) {
        return;
      }
      const nextGroups = displayedGroups.filter(
        group => !nextGroupIds.includes(group.id),
      );
      const groupNodeIdSet = new Set(nextGroupIds.map(groupId => `group:${groupId}`));
      const nextLinks = displayedLinks.filter(
        link =>
          !groupNodeIdSet.has(String(link?.from || '')) &&
          !groupNodeIdSet.has(String(link?.to || '')),
      );
      setDisplayedGroups(nextGroups);
      if (nextLinks.length !== displayedLinks.length) {
        setDisplayedLinks(nextLinks);
      }
      if (activeGroupId && nextGroupIds.includes(activeGroupId)) {
        setActiveGroupId?.(null);
      }
      if (clearAfter) {
        clearSelection();
      }
      await Promise.all([
        persistMapField('groups', nextGroups),
        nextLinks.length !== displayedLinks.length
          ? persistMapField('links', nextLinks)
          : Promise.resolve(),
      ]);
    },
    [
      activeGroupId,
      clearSelection,
      displayedGroups,
      displayedLinks,
      persistMapField,
      setActiveGroupId,
      setDisplayedGroups,
      setDisplayedLinks,
    ],
  );

  const deleteSelectedEntities = useCallback(async () => {
    const pendingDeletes = [];

    if (selectedShapeIds.length) {
      pendingDeletes.push(deleteShape(selectedShapeIds, { clearAfter: false }));
    }
    if (selectedLinkIds.length) {
      pendingDeletes.push(deleteLinks(selectedLinkIds, { clearAfter: false }));
    }
    if (selectedRegionIds.length) {
      pendingDeletes.push(deleteRegion(selectedRegionIds, { clearAfter: false }));
    }
    if (selectedPinIds.length) {
      pendingDeletes.push(deletePins(selectedPinIds, { clearAfter: false }));
    }
    if (selectedDrawingIds.length) {
      pendingDeletes.push(deleteDrawing(selectedDrawingIds, { clearAfter: false }));
    }
    if (selectedWidgetIds.length) {
      pendingDeletes.push(deleteWidget(selectedWidgetIds, { clearAfter: false }));
    }
    if (selectedGroupIds.length) {
      pendingDeletes.push(deleteGroup(selectedGroupIds, { clearAfter: false }));
    }

    if (!pendingDeletes.length) {
      return;
    }

    clearSelection();
    await Promise.all(pendingDeletes);
  }, [
    clearSelection,
    deleteDrawing,
    deleteLinks,
    deletePins,
    deleteRegion,
    deleteShape,
    deleteWidget,
    deleteGroup,
    selectedGroupIds,
    selectedDrawingIds,
    selectedLinkIds,
    selectedPinIds,
    selectedRegionIds,
    selectedShapeIds,
    selectedWidgetIds,
  ]);

  const handlePinColorCommand = useCallback(() => {
    if (!selectedPinIds.length || !pinColorCommand) {
      return;
    }
    applySelectionColor(pinColorCommand);
  }, [applySelectionColor, pinColorCommand, selectedPinIds.length]);
  useBoardCommandTick({
    tick: pinColorCommandTick,
    lastHandledTickRef: lastHandledPinColorCommandTickRef,
    onConsume: handlePinColorCommand,
  });

  const handlePinSizeCommand = useCallback(() => {
    if (!selectedPinIds.length || !pinSizeCommand) {
      return;
    }
    const nextPinIds = selectedPins
      .filter(pin => (pin?.size || 'md') !== pinSizeCommand)
      .map(pin => pin.id);
    if (!nextPinIds.length) {
      return;
    }
    updatePinsPatch(nextPinIds, { size: pinSizeCommand });
  }, [pinSizeCommand, selectedPinIds.length, selectedPins, updatePinsPatch]);
  useBoardCommandTick({
    tick: pinSizeCommandTick,
    lastHandledTickRef: lastHandledPinSizeCommandTickRef,
    onConsume: handlePinSizeCommand,
  });

  const handlePinIconCommand = useCallback(() => {
    if (!selectedPinIds.length) {
      return;
    }
    const nextIconSlug = pinIconCommand || null;
    const nextPinIds = selectedPins
      .filter(pin => (pin?.iconSlug || null) !== nextIconSlug)
      .map(pin => pin.id);
    if (!nextPinIds.length) {
      return;
    }
    updatePinsPatch(nextPinIds, { iconSlug: nextIconSlug });
  }, [pinIconCommand, selectedPinIds.length, selectedPins, updatePinsPatch]);
  useBoardCommandTick({
    tick: pinIconCommandTick,
    lastHandledTickRef: lastHandledPinIconCommandTickRef,
    onConsume: handlePinIconCommand,
  });

  const handleUnlinkItemCommand = useCallback(() => {
    const pinIdsToUpdate = selectedPins
      .filter(pin => Boolean(pin?.linkedItemId))
      .map(pin => pin.id);
    const shapeIdsToUpdate = selectedShapes
      .filter(shape => Boolean(shape?.linkedItemId))
      .map(shape => shape.id);
    const regionIdsToUpdate = selectedRegions
      .filter(region => Boolean(region?.linkedItemId))
      .map(region => region.id);
    if (!pinIdsToUpdate.length && !shapeIdsToUpdate.length && !regionIdsToUpdate.length) {
      return;
    }
    if (pinIdsToUpdate.length) {
      updatePinsPatch(pinIdsToUpdate, { linkedItemId: null });
    }
    if (shapeIdsToUpdate.length) {
      updateShapesPatch(shapeIdsToUpdate, { linkedItemId: null });
    }
    if (regionIdsToUpdate.length) {
      updateRegionsPatch(regionIdsToUpdate, { linkedItemId: null });
    }
  }, [
    selectedPins,
    selectedRegions,
    selectedShapes,
    updatePinsPatch,
    updateRegionsPatch,
    updateShapesPatch,
  ]);
  useBoardCommandTick({
    tick: unlinkItemCommandTick,
    lastHandledTickRef: lastHandledUnlinkItemCommandTickRef,
    onConsume: handleUnlinkItemCommand,
  });

  const handleOpenLinkModalCommand = useCallback(() => {
    if (!selectedPin?.id && !selectedShape?.id && !selectedRegion?.id) {
      return;
    }
    dispatch(setOpenModales({ id: pinLinkModalId, open: true }));
  }, [dispatch, pinLinkModalId, selectedPin?.id, selectedRegion?.id, selectedShape?.id]);
  useBoardCommandTick({
    tick: openLinkModalCommandTick,
    lastHandledTickRef: lastHandledOpenLinkModalCommandTickRef,
    onConsume: handleOpenLinkModalCommand,
  });

  const handleOpenBackgroundModalCommand = useCallback(() => {
    dispatch(setOpenModales({ id: backgroundModalId, open: true }));
  }, [backgroundModalId, dispatch]);
  useBoardCommandTick({
    tick: openBackgroundModalCommandTick,
    lastHandledTickRef: lastHandledOpenBackgroundModalCommandTickRef,
    onConsume: handleOpenBackgroundModalCommand,
  });

  const handleShapeTypeCommand = useCallback(() => {
    if (!selectedShapeIds.length || !shapeTypeCommand) {
      return;
    }
    const nextType = ['circle', 'rect', 'diamond', 'triangle', 'star'].includes(
      shapeTypeCommand,
    )
      ? shapeTypeCommand
      : 'circle';
    const hasEffectiveChange = selectedShapes.some(shape => shape?.type !== nextType);
    if (!hasEffectiveChange) {
      return;
    }
    updateShapesPatch(selectedShapeIds, { type: nextType });
  }, [selectedShapeIds, selectedShapes, shapeTypeCommand, updateShapesPatch]);
  useBoardCommandTick({
    tick: shapeTypeCommandTick,
    lastHandledTickRef: lastHandledShapeTypeCommandTickRef,
    onConsume: handleShapeTypeCommand,
  });

  const handleShapeColorCommand = useCallback(() => {
    if (!selectedShapeIds.length || !shapeColorCommand) {
      return;
    }
    applySelectionColor(shapeColorCommand);
  }, [applySelectionColor, selectedShapeIds.length, shapeColorCommand]);
  useBoardCommandTick({
    tick: shapeColorCommandTick,
    lastHandledTickRef: lastHandledShapeColorCommandTickRef,
    onConsume: handleShapeColorCommand,
  });

  const handleShapeLayerCommand = useCallback(() => {
    if (!selectedShapeIds.length) {
      return;
    }
    if (shapeLayerCommand !== 'front' && shapeLayerCommand !== 'back') {
      return;
    }
    const targetShapeIds = [...new Set(selectedShapeIds.filter(Boolean))];
    if (!targetShapeIds.length) {
      return;
    }
    const selectedSet = new Set(targetShapeIds);
    const movingShapes = displayedShapes
      .filter(shape => selectedSet.has(shape?.id))
      .map(shape => ({ ...shape, layer: shapeLayerCommand }));
    if (!movingShapes.length) {
      return;
    }
    const staticShapes = displayedShapes.filter(shape => !selectedSet.has(shape?.id));
    const nextShapes =
      shapeLayerCommand === 'front'
        ? [...staticShapes, ...movingShapes]
        : [...movingShapes, ...staticShapes];
    setDisplayedShapes(nextShapes);
    persistMapField('shapes', nextShapes);
  }, [
    displayedShapes,
    persistMapField,
    selectedShapeIds,
    setDisplayedShapes,
    shapeLayerCommand,
  ]);
  useBoardCommandTick({
    tick: shapeLayerCommandTick,
    lastHandledTickRef: lastHandledShapeLayerCommandTickRef,
    onConsume: handleShapeLayerCommand,
  });

  const handleLineColorCommand = useCallback(() => {
    if (!selectedLinkIds.length || !lineColorCommand) {
      return;
    }
    applySelectionColor(lineColorCommand);
  }, [applySelectionColor, lineColorCommand, selectedLinkIds.length]);
  useBoardCommandTick({
    tick: lineColorCommandTick,
    lastHandledTickRef: lastHandledLineColorCommandTickRef,
    onConsume: handleLineColorCommand,
  });

  const handleLineArrowsCommand = useCallback(() => {
    if (!selectedLinkIds.length || !lineArrowsCommand) {
      return;
    }
    const nextLinkIds = selectedLinks
      .filter(link => (link?.arrows || 'none') !== lineArrowsCommand)
      .map(link => link.id);
    if (!nextLinkIds.length) {
      return;
    }
    updateLinksPatch(nextLinkIds, { arrows: lineArrowsCommand });
  }, [lineArrowsCommand, selectedLinkIds.length, selectedLinks, updateLinksPatch]);
  useBoardCommandTick({
    tick: lineArrowsCommandTick,
    lastHandledTickRef: lastHandledLineArrowsCommandTickRef,
    onConsume: handleLineArrowsCommand,
  });

  const handleRegionColorCommand = useCallback(() => {
    if (!selectedRegionIds.length || !regionColorCommand) {
      return;
    }
    applySelectionColor(regionColorCommand);
  }, [applySelectionColor, regionColorCommand, selectedRegionIds.length]);
  useBoardCommandTick({
    tick: regionColorCommandTick,
    lastHandledTickRef: lastHandledRegionColorCommandTickRef,
    onConsume: handleRegionColorCommand,
  });

  const handleRegionBorderStyleCommand = useCallback(() => {
    if (!selectedRegionIds.length && !selectedShapeIds.length && !selectedLinkIds.length) {
      return;
    }
    applySelectionBorderStyle(regionBorderStyleCommand);
  }, [
    applySelectionBorderStyle,
    regionBorderStyleCommand,
    selectedLinkIds.length,
    selectedRegionIds.length,
    selectedShapeIds.length,
  ]);
  useBoardCommandTick({
    tick: regionBorderStyleCommandTick,
    lastHandledTickRef: lastHandledRegionBorderStyleCommandTickRef,
    onConsume: handleRegionBorderStyleCommand,
  });

  const handleDrawingColorCommand = useCallback(() => {
    if (!selectedDrawingIds.length || !drawingColorCommand) {
      return;
    }
    applySelectionColor(drawingColorCommand);
  }, [applySelectionColor, drawingColorCommand, selectedDrawingIds.length]);
  useBoardCommandTick({
    tick: drawingColorCommandTick,
    lastHandledTickRef: lastHandledDrawingColorCommandTickRef,
    onConsume: handleDrawingColorCommand,
  });

  const handleDrawingBorderStyleCommand = useCallback(() => {
    if (!selectedDrawingIds.length) {
      return;
    }
    applySelectionBorderStyle(drawingBorderStyleCommand);
  }, [
    applySelectionBorderStyle,
    drawingBorderStyleCommand,
    selectedDrawingIds.length,
  ]);
  useBoardCommandTick({
    tick: drawingBorderStyleCommandTick,
    lastHandledTickRef: lastHandledDrawingBorderStyleCommandTickRef,
    onConsume: handleDrawingBorderStyleCommand,
  });

  return {
    updatePinPatch,
    updateShapePatch,
    updateLinkPatch,
    updateRegionPatch,
    deletePins,
    deleteGroup,
    deleteSelectedEntities,
    finalizeRegionDraft,
  };
};
